/**
 * The customer's Earthworks CHANGE ORDER, opened from the emailed link with no
 * login (EW-5, fieldiq-api `GET /quote-review/change-orders/:token`). Read the
 * change, then Approve (typed name, optional title, signature) or Decline
 * (name, optional reason). Modelled on QuoteReviewPage.
 *
 * PUBLIC. Every request goes through `publicApiFetch` (api/public.ts), which
 * never sends a staff token, and both paths it is reached by are in
 * lib/public-routes.ts so a stale staff session cannot bounce the reader to
 * /login:
 *   • /quote-review/change-orders/:token — this app's route (covered by the
 *     existing /quote-review/ prefix);
 *   • /change-order-review/:token — the path the API's email actually links
 *     to (ewCoReviewUrl in fieldiq-api#1290), routed to this same page.
 *
 * 404 is "this link is not valid", 410 is "no longer open", 409 is "already
 * decided" — three screens, not one error string. The API decides everything.
 */
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AlertTriangle, CheckCircle, Loader2, XCircle } from 'lucide-react';
import { publicApiFetch, getPublicFetchMessage } from '../api/public';
import SignaturePad from '../components/SignaturePad';
import { fmtCurrency } from '../lib/display-labels';
import { formatInstant } from '../lib/calendar-date';
import { ewCanSubmitSignature } from '../lib/ew-tickets';
import type { EwCoReview } from '../types/earthworks';

type Gone = { kind: 'invalid' | 'closed'; message: string };

const INPUT = 'w-full border rounded-lg px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500';

export default function ChangeOrderReviewPage() {
  const { token } = useParams<{ token: string }>();
  const [co, setCo] = useState<EwCoReview | null>(null);
  const [loading, setLoading] = useState(true);
  const [gone, setGone] = useState<Gone | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [mode, setMode] = useState<'accept' | 'decline' | null>(null);
  const [name, setName] = useState('');
  const [title, setTitle] = useState('');
  const [reason, setReason] = useState('');
  const [signature, setSignature] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await publicApiFetch(`/quote-review/change-orders/${token}`);
      const body = await res.json().catch(() => ({}));
      if (res.status === 404) setGone({ kind: 'invalid', message: 'This change order link is not valid. Please ask for a new one.' });
      else if (res.status === 410) setGone({ kind: 'closed', message: body?.error || 'This change order is no longer open. Please ask for a new link.' });
      else if (!res.ok) setError(body?.error || 'The change order could not be loaded. Please try again.');
      else setCo(body.data as EwCoReview);
    } catch (err) {
      setError(getPublicFetchMessage(err, 'Unable to connect. Please try again.'));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { void load(); }, [load]);

  const decide = async (kind: 'accept' | 'decline') => {
    setError(null);
    setNotice(null);
    if (!name.trim()) { setError(kind === 'accept' ? 'Type your name to approve the change order.' : 'Type your name to decline the change order.'); return; }
    if (kind === 'accept' && !signature) { setError('Sign in the box, then press Confirm signature.'); return; }
    setSubmitting(true);
    try {
      const body = kind === 'accept'
        ? { name: name.trim(), title: title.trim() || undefined, signature }
        : { name: name.trim(), reason: reason.trim() || undefined };
      const res = await publicApiFetch(`/quote-review/change-orders/${token}/${kind}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const out = await res.json().catch(() => ({}));
      if (res.ok || res.status === 409) {
        // 409: decided already (here or elsewhere) — the reload shows the decision.
        setMode(null);
        if (res.status === 409) setNotice(out?.error || 'This change order has already been decided.');
        await load();
      } else if (res.status === 410) {
        setGone({ kind: 'closed', message: out?.error || 'This change order is no longer open. Please ask for a new link.' });
      } else if (res.status === 404) {
        setGone({ kind: 'invalid', message: 'This change order link is not valid. Please ask for a new one.' });
      } else {
        setError(out?.error || 'That did not go through. Please try again.');
      }
    } catch (err) {
      setError(getPublicFetchMessage(err, 'Unable to connect. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading && !co) {
    return (
      <div className="min-h-full bg-gray-50 flex items-center justify-center" data-testid="co-review-loading">
        <Loader2 className="w-8 h-8 text-amber-600 animate-spin" />
      </div>
    );
  }

  if (gone || (!co && error)) {
    return (
      <div className="min-h-full bg-gray-50">
        <div className="max-w-3xl mx-auto px-6 py-12">
          <div className="bg-white rounded-xl shadow-sm border p-8 text-center" data-testid="co-review-gone" data-kind={gone?.kind ?? 'error'}>
            <AlertTriangle className="w-14 h-14 text-amber-500 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-gray-900 mb-2">
              {gone?.kind === 'closed' ? 'This change order is no longer open' : gone ? 'Link not valid' : 'Something went wrong'}
            </h1>
            <p className="text-gray-600">{gone?.message ?? error}</p>
          </div>
        </div>
      </div>
    );
  }
  if (!co) return null;

  const cur = co.currency === 'CAD' || co.currency === 'USD' ? co.currency : null;
  const money = (v: number | null | undefined) => fmtCurrency(v, cur);
  const decided = co.decision;
  const accent = co.company.primaryColor || '#1e293b';
  const companyName = co.company.name ?? 'The company';

  return (
    <div className="min-h-full bg-gray-50" data-testid="co-review-page">
      <div className="bg-white border-b px-6 py-4" style={{ borderTop: `4px solid ${accent}` }}>
        <div className="max-w-5xl mx-auto flex items-center gap-3">
          {co.company.logoUrl && <img src={co.company.logoUrl} alt="" className="h-9" />}
          <div>
            <h1 className="font-bold text-gray-900">{co.company.name}</h1>
            <p className="text-sm text-gray-500">Change order {co.coNumber} · Job {co.jobNumber}</p>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-6 space-y-4">
        {notice && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-amber-800 text-sm" role="status" data-testid="co-review-notice">{notice}</div>
        )}
        {decided && (
          <div data-testid="co-review-decided"
               className={`rounded-xl border p-5 flex items-start gap-3 ${decided.decision === 'accepted' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
            {decided.decision === 'accepted'
              ? <CheckCircle className="w-6 h-6 text-green-600 shrink-0" />
              : <XCircle className="w-6 h-6 text-red-600 shrink-0" />}
            <div>
              <p className="font-semibold text-gray-900">
                {decided.decision === 'accepted' ? 'Change order approved' : 'Change order declined'}
                {decided.signerName ? ` by ${decided.signerName}` : ''}
                {decided.signerTitle ? `, ${decided.signerTitle}` : ''}
              </p>
              <p className="text-sm text-gray-600">
                {formatInstant(decided.decidedAt, co.company.timeZone) ?? ''}
                {decided.declineReason ? ` — “${decided.declineReason}”` : ''}
              </p>
              <p className="text-sm text-gray-600 mt-1">
                {decided.decision === 'accepted'
                  ? `Thank you. ${companyName} has your approval.`
                  : `Thank you for letting ${co.company.name ?? 'the company'} know.`}
              </p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl shadow-sm border p-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div><p className="text-gray-500">Prepared for</p><p className="font-semibold text-gray-900">{co.customer.name ?? '—'}</p></div>
            <div><p className="text-gray-500">Site</p><p className="font-semibold text-gray-900">{co.siteName ?? '—'}</p></div>
            <div><p className="text-gray-500">Link expires</p><p className="font-semibold text-gray-900">{formatInstant(co.expiresAt, co.company.timeZone) ?? '—'}</p></div>
            <div><p className="text-gray-500">Amount</p><p className="font-bold text-lg text-gray-900" data-testid="co-review-total">{money(co.total)}</p></div>
          </div>
          <div className="mt-4 text-sm text-gray-700">
            <p className="font-semibold text-gray-900">{co.reason}</p>
            {co.description && <p className="mt-1 whitespace-pre-line">{co.description}</p>}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border">
          <ul className="divide-y" data-testid="co-review-lines">
            {co.lines.map((l, i) => (
              <li key={i} className="px-4 py-3 text-sm">
                <div className="flex items-start justify-between gap-3">
                  <span className="text-gray-900 min-w-0 break-words">{l.description}</span>
                  <span className="font-medium text-gray-900 whitespace-nowrap">{money(l.amount)}</span>
                </div>
                <p className="mt-0.5 text-xs text-gray-500">
                  {l.quantity ?? '—'} × {money(l.rate)} · {l.unitLabel || l.unit || '—'}
                </p>
              </li>
            ))}
          </ul>
          <div className="border-t px-4 py-3 text-sm font-bold text-gray-900 flex justify-between sm:ml-auto sm:max-w-xs">
            <span>Total</span><span>{money(co.total)}</span>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-red-700 text-sm" role="alert">{error}</div>
        )}

        {!decided && co.status === 'open' && (
          <div className="bg-white rounded-xl shadow-sm border p-5 space-y-4" data-testid="co-review-decide">
            {mode === null && (
              <div className="flex flex-wrap gap-3">
                <button type="button" onClick={() => { setMode('accept'); setError(null); }}
                        className="inline-flex items-center gap-2 bg-green-600 text-white rounded-lg px-5 py-2.5 font-semibold hover:bg-green-700">
                  <CheckCircle className="w-5 h-5" /> Approve change order
                </button>
                <button type="button" onClick={() => { setMode('decline'); setError(null); }}
                        className="inline-flex items-center gap-2 border border-gray-300 text-gray-700 rounded-lg px-5 py-2.5 font-semibold hover:bg-gray-50">
                  <XCircle className="w-5 h-5" /> Decline
                </button>
              </div>
            )}

            {mode && (
              <>
                <h2 className="font-bold text-gray-900">{mode === 'accept' ? 'Approve this change order' : 'Decline this change order'}</h2>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <label htmlFor="cor-name" className="block text-xs text-gray-500 mb-1">Your name</label>
                    <input id="cor-name" className={INPUT} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. John Smith" />
                  </div>
                  {mode === 'accept' ? (
                    <div>
                      <label htmlFor="cor-title" className="block text-xs text-gray-500 mb-1">Title (optional)</label>
                      <input id="cor-title" className={INPUT} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Site superintendent" />
                    </div>
                  ) : (
                    <div>
                      <label htmlFor="cor-reason" className="block text-xs text-gray-500 mb-1">Reason (optional)</label>
                      <input id="cor-reason" className={INPUT} value={reason} onChange={(e) => setReason(e.target.value)} />
                    </div>
                  )}
                </div>
                {mode === 'accept' && (
                  <SignaturePad tone="document" label="Signature" value={signature ?? undefined}
                                onSave={setSignature} onClear={() => setSignature(null)} />
                )}
                {mode === 'accept' && (
                  <p className="text-xs text-gray-500">
                    By signing you approve change order {co.coNumber} for {money(co.total)}.
                  </p>
                )}
                <div className="flex flex-wrap gap-3">
                  <button type="button" data-testid="co-review-submit"
                          disabled={submitting || (mode === 'accept' ? !ewCanSubmitSignature(name, signature) : !name.trim())}
                          onClick={() => decide(mode)}
                          className={`inline-flex items-center gap-2 rounded-lg px-5 py-2.5 font-semibold text-white disabled:opacity-60 ${mode === 'accept' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}>
                    {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                    {mode === 'accept' ? 'Sign and approve' : 'Decline change order'}
                  </button>
                  <button type="button" disabled={submitting} onClick={() => { setMode(null); setError(null); }}
                          className="border border-gray-300 text-gray-700 rounded-lg px-5 py-2.5 font-semibold hover:bg-gray-50">
                    Back
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
